import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { documentStoreService } from '@/services/document-store.service';
import { DocumentStore, DocumentProgressStatusEnum } from '@/types/document-store.types';

interface UseRealTimeStatusOptions {
  storeId: string;
  enabled?: boolean;
  pollInterval?: number;
}

/**
 * Hook for real-time status monitoring of document stores and their documents
 */
export function useRealTimeStatus({ storeId, enabled = true, pollInterval = 5000 }: UseRealTimeStatusOptions) {
  const queryClient = useQueryClient();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data: store, isLoading, error } = useQuery({
    queryKey: ['document-store', storeId],
    queryFn: () => documentStoreService.getDocumentStoreById(storeId),
    enabled: enabled && !!storeId,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache
  });

  // Calculate processing stats
  const processingStats = store ? {
    total: store.document_urls.length,
    processing: store.document_urls.filter(d => d.status === DocumentProgressStatusEnum.IN_PROGRESS).length,
    completed: store.document_urls.filter(d => d.status === DocumentProgressStatusEnum.DONE).length,
    failed: store.document_urls.filter(d => d.status === DocumentProgressStatusEnum.FAILED).length,
    hasProcessing: store.document_urls.some(d => d.status === DocumentProgressStatusEnum.IN_PROGRESS),
    overallStatus: store.status,
  } : null;

  // Set up real-time polling when there are documents being processed
  useEffect(() => {
    if (!enabled || !storeId || !processingStats?.hasProcessing) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Start polling if we have processing documents
    intervalRef.current = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: ['document-store', storeId],
      });
    }, pollInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [storeId, enabled, pollInterval, processingStats?.hasProcessing, queryClient]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    store,
    processingStats,
    isLoading,
    error,
    isPolling: !!intervalRef.current,
  };
}

/**
   * Hook for monitoring multiple document stores with server-side pagination and filtering
   */
  export function useRealTimeStoresList(
    enabled = true,
    pollInterval = 10000,
    skip = 0,
    limit = 100,
    searchQuery?: string
  ) {
    const queryClient = useQueryClient();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const { data: storesData, isLoading, error } = useQuery({
      queryKey: ['document-stores-list', skip, limit, searchQuery],
      queryFn: () => documentStoreService.listDocumentStores({
        skip,
        limit,
        ...(searchQuery && { name: searchQuery })
      }),
      enabled,
      staleTime: 0,
      gcTime: 0,
    });

    const stores = storesData?.data || [];
    const totalRecords = storesData?.meta_data?.total_records || 0;

    // Calculate overall processing stats from current page data
    const overallStats = {
      totalStores: totalRecords, // Use total from server instead of current page length
      storesWithProcessing: stores.filter(store =>
        store.document_urls.some(d => d.status === DocumentProgressStatusEnum.IN_PROGRESS)
      ).length,
      totalDocuments: stores.reduce((sum, store) => sum + store.document_urls.length, 0),
      processingDocuments: stores.reduce((sum, store) =>
        sum + store.document_urls.filter(d => d.status === DocumentProgressStatusEnum.IN_PROGRESS).length, 0    
      ),
      completedDocuments: stores.reduce((sum, store) =>
        sum + store.document_urls.filter(d => d.status === DocumentProgressStatusEnum.DONE).length, 0
      ),
      failedDocuments: stores.reduce((sum, store) =>
        sum + store.document_urls.filter(d => d.status === DocumentProgressStatusEnum.FAILED).length, 0
      ),
    };

    // Set up polling when there are processing documents
    useEffect(() => {
      if (!enabled || overallStats.processingDocuments === 0) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      intervalRef.current = setInterval(() => {
        queryClient.invalidateQueries({
          queryKey: ['document-stores-list'],
        });
      }, pollInterval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }, [enabled, overallStats.processingDocuments, pollInterval, queryClient]);

    // Clean up on unmount
    useEffect(() => {
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, []);

    return {
      stores,
      totalRecords,
      overallStats,
      isLoading,
      error,
      isPolling: !!intervalRef.current,
    };
  }

/**
 * Hook for individual document status monitoring
 */
export function useDocumentStatus(storeId: string, documentId: string, enabled = true) {
  const { store, processingStats, isLoading } = useRealTimeStatus({ 
    storeId, 
    enabled,
    pollInterval: 3000, // More frequent polling for individual docs
  });

  const document = store?.document_urls.find(d => d._id === documentId);
  
  const documentStats = document ? {
    status: document.status,
    isProcessing: document.status === DocumentProgressStatusEnum.IN_PROGRESS,
    isCompleted: document.status === DocumentProgressStatusEnum.DONE,
    isFailed: document.status === DocumentProgressStatusEnum.FAILED,
    useAsExample: document.useAsExample,
    lastUpdated: document.updatedAt,
  } : null;

  return {
    document,
    documentStats,
    storeStats: processingStats,
    isLoading,
  };
}

/**
 * Utility function to get status color and label
 */
export function getStatusDisplay(status: DocumentProgressStatusEnum | string) {
  switch (status) {
    case DocumentProgressStatusEnum.DONE:
      return {
        color: 'green',
        label: 'Completed',
        bgColor: 'bg-green-100',
        textColor: 'text-green-700',
        dotColor: 'bg-green-500',
      };
    case DocumentProgressStatusEnum.IN_PROGRESS:
      return {
        color: 'blue',
        label: 'Processing',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-700',
        dotColor: 'bg-blue-500',
      };
    case DocumentProgressStatusEnum.FAILED:
      return {
        color: 'red',
        label: 'Failed',
        bgColor: 'bg-red-100',
        textColor: 'text-red-700',
        dotColor: 'bg-red-500',
      };
    default:
      return {
        color: 'gray',
        label: 'Unknown',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-700',
        dotColor: 'bg-gray-500',
      };
  }
}